/**
 * ORDER → PROPOSAL SHEET adapter (Pete 2026-10-28: "wire it into the proposal
 * flow so reps can generate this from any order").
 *
 * One entry point turns the live order API payloads — orderForm + dxfSki
 * wire arcs + calcSki1 edgeFrame + the Shopify draft invoice URL — into the
 * rendered proposal sheet PDF. Pure and deterministic: same inputs ->
 * identical bytes. Runs in node (CLI scripts/render-order-sheet.ts) AND in
 * the browser (the Proposal & payment tab's one-click generator) — no node
 * builtins, fonts/logos are embedded base64.
 *
 * Every number is sourced; nothing invented:
 * - contacts: production calc engine formula replicated against the
 *   calcSki1 edge frame (VALIDATED: reproduces 230702's Geometry tab
 *   210.04/61.15 nose + 149.59/27.65 tail to 0.01mm).
 * - end heights: engine arc formula h = R(1 − cos(L/R)), R = the EMBEDDED
 *   noseBlock/tailBlock upturnRadius (never name/code guesses — ids-vs-codes
 *   swap trap).
 * - waist: measured from the LIVE DXF outline when the form carries none
 *   (nullable-dims trap) so the sheet, arrows, and ride paragraph agree.
 */
import { buildSpecSheet, type SpecSheetInput } from './specSheet';
import { renderSpecSheetPdf, samplePlanViewPolyline } from './pdf/specSheetPdf';
import { buildRenderFacetFromWire, type RenderFacet } from './renderFacet';

export interface OrderProposalPayload {
  /** orderForm row (child collection items[0]) — embedded material/block refs. */
  form: Record<string, unknown>;
  /** dxfSki facet arcs1 (raw wire arcs; the adapter normalizes). */
  dxfArcs: Array<Record<string, unknown>>;
  /** calcSki1 edgeFrame (contact geometry). */
  edgeFrame: Record<string, number>;
  /** Shopify draft-order invoice URL (PRICING section; absent = hidden). */
  invoiceUrl?: string;
  /** Pete 2026-10-28: the designer's edited HOW THEY'LL RIDE text. undefined
   *  = auto-draft (draftRideParagraph); '' = section hidden; any other
   *  string = used verbatim on the sheet. */
  rideText?: string;
  /** Overrides form.handcraftedFor when the caller knows better. */
  customerName?: string;
  designVersion?: number;
}

const num = (v: unknown): number | undefined => {
  const n = Number(v);
  return Number.isFinite(n) && v !== null && v !== '' ? n : undefined;
};
const adj = (v: unknown): number => num(v) ?? 0;

const CAMBER_MM: Record<string, number> = { '-1': -5, '0': 0, '1': 3, '2': 5, '3': 8 };

/** Measure the LIVE DXF outline: waist + end-to-widest stations.
 * Normalize the wire arcs FIRST (buildRenderFacetFromWire mirrors d=-1) —
 * sampling raw wire arcs gives garbage y. Widths are hi-lo around the
 * centerline, not 2*max|y| (the outline is NOT symmetric about y=0). */
export function measureDxfOutline(dxfArcs: Array<Record<string, unknown>>): {
  poly: Array<[number, number]>;
  minX: number; maxX: number; span: number;
  widthAt: (x: number) => number;
  waistX: number; waistW: number;
  noseWidest: { w: number; fromEnd: number };
  tailWidest: { w: number; fromEnd: number };
} {
  const prov = buildRenderFacetFromWire({
    wireArcs: dxfArcs,
    key: { tailX: 0, waistX: 915, noseX: 1830, tailWidthMm: 1, waistWidthMm: 1, noseWidthMm: 1 },
    profile: {} as never,
  });
  const poly = samplePlanViewPolyline(prov.arcs1);
  const xs = poly.map((p) => p[0]);
  const minX = Math.min(...xs), maxX = Math.max(...xs);
  const span = maxX - minX;
  const widthAt = (x: number): number => {
    let lo = Infinity, hi = -Infinity;
    for (const [px, py] of poly) if (Math.abs(px - x) <= 2) { lo = Math.min(lo, py); hi = Math.max(hi, py); }
    return hi - lo;
  };
  // waist: scan the MIDDLE only (end zones narrow toward the caps)
  let waistX = minX, waistW = Infinity;
  for (let x = minX + span * 0.3; x <= maxX - span * 0.3; x += 1) {
    const w = widthAt(x);
    if (w < waistW) { waistW = w; waistX = x; }
  }
  const noseWidest = (() => {
    let best = 0, bx = minX;
    for (let x = minX; x < minX + 350; x += 1) { const w = widthAt(x); if (w > best) { best = w; bx = x; } }
    return { w: best, fromEnd: bx - minX };
  })();
  const tailWidest = (() => {
    let best = 0, bx = maxX;
    for (let x = maxX; x > maxX - 350; x -= 1) { const w = widthAt(x); if (w > best) { best = w; bx = x; } }
    return { w: best, fromEnd: maxX - bx };
  })();
  return { poly, minX, maxX, span, widthAt, waistX, waistW, noseWidest, tailWidest };
}

/**
 * AUTO-DRAFT of the HOW THEY'LL RIDE paragraph (Pete 2026-10-28: "auto
 * generate a first draft", then the ski designer fully edits it in the
 * Proposal tab's text box). Deterministic: same form + outline -> same
 * text. The Sales Recommendation structure the legacy Ski Proposal printed
 * at the bottom, rebuilt from the order form's own descriptors + dims.
 */
export function draftRideParagraph(
  form: Record<string, unknown>,
  dxfArcs: Array<Record<string, unknown>>,
  edgeFrame?: Record<string, unknown>,
): string {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const of = form as Record<string, any>;
  const { waistW } = measureDxfOutline(dxfArcs);
  // Spec-first waist (Pete 2026-10-30): form fields, then the calc engine,
  // never the DXF outline minimum as the customer-facing number.
  const specWaist = num(of.waistWidth) ?? num(of.calcWaistWidth)
    ?? num((edgeFrame ?? {}).waistWidth) ?? waistW;
  const desc1 = String(of.skiDescriptor1 ?? '');
  const desc2 = String(of.skiDescriptor2 ?? '');
  const lengthMm = num(of.length)!;
  const lengthCm = Math.floor(lengthMm / 10 + 0.5);
  const radiusM = num(of.sidecutRadius)! / 1000;
  const waistLabel = Math.round(specWaist);
  return [
    `This is a ${desc1} ski design.`,
    `The ${lengthCm}cm length, ${waistLabel}mm waist, and rockered tip will allow you to easily float in powder, broken, and variable snow conditions.`,
    `The ${radiusM.toFixed(1)}m sidecut radius, balanced flex pattern, and stiffness that is calibrated to your size will enable you to get a wide array of turn shapes out of the skis.`,
    `They'll be forgiving with easy turn initiation and easy turn release in tight terrain such as trees, moguls, and steeps.`,
    `They'll be predictable and stable at speed and on hard and groomed snow.`,
    `This design will work well in a broad range of terrain types and snow conditions.`,
    `This is an ideal ${desc2} ski design for you.`,
  ].join(' ');
}

export function buildOrderProposalSheetInput(payload: OrderProposalPayload): {
  input: SpecSheetInput;
  facet: RenderFacet;
} {
  const { form, dxfArcs, edgeFrame: ef, invoiceUrl } = payload;
  const of = form as Record<string, any>; // eslint-disable-line @typescript-eslint/no-explicit-any

  // ---- profile numbers: engine formula, embedded block radii (P0-GEOM) ----
  const noseR = Number(of.noseBlock.upturnRadius);
  const tailR = Number(of.tailBlock.upturnRadius);
  const noseContact = ef.noseLength + ef.noseEntryXFromNERCenter + adj(of.noseInwardAdjustment);
  const tailContact = ef.tailLength + ef.tailEntryXFromTERCenter + adj(of.tailInwardAdjustment);
  const h = (R: number, L: number): number => R * (1 - Math.cos(L / R));
  const noseHeight = h(noseR, noseContact);
  const tailHeight = h(tailR, tailContact);
  const lengthMm = num(of.length)!;
  const camberEnum = String(of.camber);
  const camberHeight = CAMBER_MM[camberEnum] ?? 3;

  // ---- plan-view key + widest stations: measure the LIVE DXF outline ----
  const m = measureDxfOutline(dxfArcs);
  const { poly, minX, maxX, span, widthAt } = m;
  const waistX = m.waistX, waistW = m.waistW;
  const noseWidest = m.noseWidest, tailWidest = m.tailWidest;
  // Pete 2026-10-30 ('wrong waist width - this should never happen'): the
  // DISPLAYED waist is the SPEC value - the form's own waist fields, then
  // the calc engine's edgeFrame.waistWidth (the number the order name
  // carries, e.g. '172-88'), NEVER the DXF outline minimum (CAD outlines
  // run ~4.5mm UNDER spec; the DXF reading is station geometry only).
  const specWaistMm = num(of.waistWidth) ?? num(of.calcWaistWidth)
    ?? num((ef as Record<string, unknown>).waistWidth) ?? waistW;

  // Map the measured waist station into key space by span fraction
  // (validated on 230702: renders correctly against the outline arrows).
  const waistStationKey = Math.round(((waistX - minX) / span) * lengthMm);
  const facet = buildRenderFacetFromWire({
    wireArcs: dxfArcs,
    key: {
      tailX: 0, waistX: waistStationKey, noseX: lengthMm,
      tailWidthMm: num(of.tailWidth) ?? tailWidest.w,
      waistWidthMm: waistW,
      noseWidthMm: num(of.noseWidth) ?? noseWidest.w,
    },
    profile: {
      noseHeightMm: noseHeight,
      noseLengthToContactMm: noseContact,
      noseRadiusMm: noseR,
      tailHeightMm: tailHeight,
      tailLengthToContactMm: tailContact,
      tailRadiusMm: tailR,
      runningLengthMm: num(ef.effectiveEdge) ?? 0,
      camberEnum,
      camberHeightMm: camberHeight,
      tipRockerName: (of.tipRocker ?? {}).name ?? '',
      tailRockerName: (of.tailRocker ?? {}).name ?? '',
      noseInwardAdjustmentMm: num(of.noseInwardAdjustment) ?? 0,
      tailInwardAdjustmentMm: num(of.tailInwardAdjustment) ?? 0,
      noseWidestFromEndMm: noseWidest.fromEnd,
      tailWidestFromEndMm: tailWidest.fromEnd,
      lengthMm,
      noseWidthMm: num(of.noseWidth) ?? noseWidest.w,
      waistWidthMm: specWaistMm,
      tailWidthMm: num(of.tailWidth) ?? tailWidest.w,
      sidecutRadiusM: num(of.sidecutRadius)! / 1000,
    },
  });

  const name = payload.customerName ?? String(of.handcraftedFor ?? '');
  const desc1 = String(of.skiDescriptor1 ?? '');
  const desc2 = String(of.skiDescriptor2 ?? '');
  const flex = String(of.flexPattern ?? 'Balanced');
  const stiff = String((of.overallStiffness ?? {}).name ?? 'Medium');
  // structuralLayer.NAME is the short label; the CONSTRUCTION sentence lives
  // in .description (Pete 2026-10-28: incomplete without it).
  const core = (of.coreMaterial ?? {}).description ?? (of.coreMaterial ?? {}).name;
  const structural = (of.structuralLayer ?? {}).description
    ?? (of.structuralLayer ?? {}).upgradeDescription
    ?? (of.structuralLayer ?? {}).name;
  const bindings = (of.bindingType ?? {}).name;
  // Pete 2026-10-28: the Top Graphic field is rep free-text — rewrite it to
  // customer-facing copy before it lands on the proposal sheet.
  const graphic = of.topGraphic ? topGraphicToCustomerCopy(String(of.topGraphic)) : null;
  const lengthCm = Math.floor(lengthMm / 10 + 0.5);
  const radiusM = num(of.sidecutRadius)! / 1000;

  const input: SpecSheetInput = {
    designVersion: payload.designVersion ?? 1,
    customerName: name,
    serial: undefined, // proposals are pre-build: no serial (Pete 2026-10-15)
    state: {
      skiType: String(of.skiType ?? 'All-Mountain'),
      length: lengthCm,
      noseWidth: num(of.noseWidth) ?? noseWidest.w,
      tailWidth: num(of.tailWidth) ?? tailWidest.w,
      // waist: the form often carries none (nullable-dims trap) — the LIVE
      // DXF-measured waist keeps the sheet, arrows, and ride paragraph true.
      waistWidthRecommendation: Math.round(specWaistMm),
      flexPattern: flex,
      noseBlockId: of.noseBlock.id, tailBlockId: of.tailBlock.id,
    } as never,
    facet,
    sidecutRadiusM: radiusM,
    construction: {
      coreDescription: core,
      structuralDescription: structural,
      baseDescription: ((of.baseMaterial ?? {}).upgradeDescription) ?? (of.baseMaterial ?? {}).description,
      aramid: of.aramid === true ? 'Aramid crossweave' : undefined,
      topsheetDescription: of.topMaterial ? String(of.topMaterial) : undefined,
      flexPattern: flex,
      stiffness: stiff,
      // materialsStack = SHORT layer names for the laminate-art labels —
      // NEVER the description sentence (Pete 2026-10-28 attachment).
      materialsStack: ['Topsheet', 'Fiberglass', (of.structuralLayer ?? {}).name ?? 'Structural',
        'Wood Core', 'Fiberglass',
        ...(of.aramid === true ? ['Aramid'] : []), 'Base Material'].filter(Boolean) as string[],
      bindings: bindings && !/^(NONE|OTHER)\b/i.test(bindings.trim()) ? bindings.trim() : '',
      graphic: graphic ?? undefined,
      // Pete 2026-10-30: Silver/Ultra/Flow bundle upgrades under STIFFNESS.
      packageUpgrades: packageUpgradesFor(of) ?? undefined,
      // Pete 2026-11-03: MATERIAL LAYUP section - the real layer stack.
      materialLayup: materialLayupFor(of),
    },
    // Core thickness — REQUIRED for the sideview curve: the renderer draws
    // the ski envelope only when all three thicknesses are sourced.
    coreThickness: {
      noseMm: num(of.coreThicknessNose),
      waistMm: num(of.coreThicknessWaist),
      tailMm: num(of.coreThicknessTail),
      tipTaper: String(of.coreTipTaper ?? ''),
      tailTaper: String(of.coreTailTaper ?? ''),
    },
    // Without tip/tail descriptions the renderer skips the whole TIP/TAIL
    // block (if-guarded) — Pete 2026-10-28: "missing under PROFILE".
    tipDescription: String(of.tipDescription ?? ''),
    tailDescription: String(of.tailDescription ?? ''),
    graphicsDescription: graphic ?? undefined,
    graphicsStatus: 'pending',
    pricingInvoiceUrl: invoiceUrl,
    // Pete 2026-10-28: AUTO-DRAFT first (draftRideParagraph), then the ski
    // designer fully edits it in the Proposal tab text box — rideText is the
    // edited copy (undefined = auto-draft; '' = section hidden).
    designSummary: payload.rideText ?? draftRideParagraph(of, dxfArcs, ef),
    anchors: {
      missionStatement: `Design a ${desc1} ski for ${desc2} days.`,
      skierDescription: name,
    },
  };

  return { input, facet };
}

/** Full render: live order payloads -> proposal sheet PDF bytes. */
export function renderOrderProposalSheet(payload: OrderProposalPayload): {
  bytes: Uint8Array;
  fileName: string;
} {
  const { input, facet } = buildOrderProposalSheetInput(payload);
  const data = buildSpecSheet(input);
  const bytes = renderSpecSheetPdf(data, facet.arcs1);
  const who = input.customerName.replace(/[^A-Za-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'Customer';
  return { bytes, fileName: `Wagner-Proposal-${who}.pdf` };
}

/**
 * Pete 2026-10-28: `topGraphic` is rep-to-rep vocabulary on the order form
 * ("let me know your thoughts", "To be determined..."). The proposal sheet is
 * customer-facing — rewrite the known rep phrasings to second person and pass
 * already-customer-facing text through untouched.
 */
/**
 * Pete 2026-11-03: MATERIAL LAYUP - the ski's layer stack, TOP material
 * first, working down, derived from the order form (never hand-copied).
 * Verified verbatim against Pete's three examples:
 *   Silver + Titanal:  Topsheet/Fiberglass/Titanal/Wood Core/Titanal/
 *                      Fiberglass/VDS/Aramid/Base with Steel Edges
 *   Silver + CarbonWrap: Topsheet/Fiberglass/Wood Core with Carbon Fiber
 *                      Torsion Box/Fiberglass/VDS/Aramid/Base with Steel Edges
 *   Ultra + CarbonWrap:  .../Visco Dampening/Wood Core with Carbon Fiber
 *                      Torsion Box/Visco Dampening/Fiberglass/...
 * Semantics:
 *   - structuralLayer id 2 Titanal: a sheet ABOVE and BELOW the core.
 *   - id 3 CarbonWrap: merges into the core line ('Wood Core with Carbon
 *     Fiber Torsion Box').
 *   - id 1 Fiberglass: no extra line (the skin is already listed).
 *   - id 4 Countervail: carbon-family (reference table: 'visco-elastically
 *     dampened Carbon Fiber structural layers'), merges into the core line
 *     ('Wood Core with Countervail') - no live order uses it; flagged to Pete.
 *   - Visco Dampening: ULTRA package only (the Ultra press note appends
 *     'Add visco layers to the cores'), one line either side of the core,
 *     INSIDE the structural sheets (Pete's Ultra example).
 *   - VDS: standard in every Wagner layup (Pete's examples; no form field).
 *   - Aramid: orderForm.aramid === true, independent of package.
 */
export function materialLayupFor(of: Record<string, any>): string[] {  // eslint-disable-line @typescript-eslint/no-explicit-any
  const st = String((of.structuralLayer ?? {}).name ?? '');
  const code = String((of.package ?? {}).code ?? '').toUpperCase();
  const visco = code === 'ULTRA';
  // 2026-11-03 data check (live reference table + order sample): only
  // Titanal is a SHEET layer; Countervail is carbon-family ('Countervail
  // visco-elastically dampened Carbon Fiber structural layers') and no live
  // order uses it - it merges into the core line like CarbonWrap.
  const sheetLayer = /titanal/i.test(st) ? 'Titanal' : null;
  const coreLine = /titanal/i.test(st) ? 'Wood Core'
    : /carbonwrap/i.test(st) ? 'Wood Core with Carbon Fiber Torsion Box'
    : /countervail/i.test(st) ? 'Wood Core with Countervail'
    : 'Wood Core';
  const stack: string[] = ['Topsheet', 'Fiberglass'];
  // Visco rides AGAINST the core (Ultra press note: 'Add visco layers to the
  // cores') - i.e. INSIDE the Titanal sheets, not outside them.
  if (sheetLayer) stack.push(sheetLayer);
  if (visco) stack.push('Visco Dampening');
  stack.push(coreLine);
  if (visco) stack.push('Visco Dampening');
  if (sheetLayer) stack.push(sheetLayer);
  // Pete 2026-11-03 UPDATE: VDS always sits directly ON TOP of the Base
  // with Steel Edges, and Aramid always sits on top of the VDS - so the
  // tail of the stack reads .../Fiberglass/Aramid/VDS/Base with Steel
  // Edges. (Supersedes the VDS-then-Aramid order in his three examples.)
  stack.push('Fiberglass');
  if (of.aramid === true) stack.push('Aramid');
  stack.push('VDS', 'Base with Steel Edges');
  return stack;
}

/**
 * Pete 2026-10-30: package upgrades block for YOUR BUILD. When the order's
 * package is an upgrade bundle, the sheet gains a header line under STIFFNESS
 * ('SILVER PACKAGE UPGRADES:' / 'ULTRA PACKAGE UPGRADES:' / 'FLOW BUNDLE
 * UPGRADES:') followed by that bundle's material upgrades:
 *   - BASE MATERIAL: all three bundles (baseMaterial upgrade copy)
 *   - CONSTRUCTION: Silver + Ultra only (Titanal / CarbonWrap description)
 *   - ARAMID: all three (when orderForm.aramid === true)
 *   - CALIBRATED VISCO DAMPENING: Ultra only (draft copy — Pete veto pending)
 * Base/Factory Basic/Other packages render no block.
 */
export function packageUpgradesFor(of: Record<string, any>): {  // eslint-disable-line @typescript-eslint/no-explicit-any
  header: string;
  /** Benefit sentences in order — NO per-upgrade labels (Pete 2026-10-30). */
  lines: string[];
} | null {
  const code = String((of.package ?? {}).code ?? '').toUpperCase();
  const headers: Record<string, string> = {
    SILVER: 'SILVER PACKAGE UPGRADES',
    ULTRA: 'ULTRA PACKAGE UPGRADES',
    FLOW: 'FLOW BUNDLE UPGRADES',
  };
  if (!(code in headers)) return null;
  const lines: string[] = [];
  const fix = (t?: string | null) =>
    t ? (t.trim().endsWith('.') ? t.trim() : `${t.trim()}.`) : null;
  // Pete 2026-10-30 exact example copy (Brad Blue / Silver):
  // 'World Cup Base for improved glide efficiency. Titanal (metal)
  //  construction for improved stability and edge control. Impact
  //  resistance package.' — sentences only, no BASE MATERIAL:/CONSTRUCTION:/
  //  ARAMID: labels.
  const base = (of.baseMaterial ?? {}) as { upgradeDescription?: string; description?: string };
  lines.push(fix(base.upgradeDescription ?? base.description) ?? 'Upgraded base material.');
  if (code === 'SILVER' || code === 'ULTRA') {
    const st = (of.structuralLayer ?? {}) as { name?: string; description?: string };
    const nm = String(st.name ?? '');
    if (/titanal/i.test(nm)) {
      lines.push('Titanal (metal) construction for improved stability and edge control.');
    } else if (/carbon/i.test(nm)) {
      lines.push(`${nm} construction for lightweight power and quick response.`.replace(/\s+/g, ' '));
    } else {
      lines.push(fix(st.description) ?? 'Upgraded construction.');
    }
  }
  if (of.aramid === true) {
    lines.push('Impact resistance package.');
  }
  if (code === 'ULTRA') {
    // draft copy — Pete veto still pending
    lines.push('Calibrated visco dampening for a smooth, quiet ride.');
  }
  if (!lines.length) return null;
  return { header: headers[code], lines };
}

export function topGraphicToCustomerCopy(raw: string): string {
  const t = raw.trim();
  if (/^(to be determined|tbd|not (yet )?(chosen|selected|determined))/i.test(t)
      || /\b(let me know|let's talk|check out www\.wagnerskis\.com\/gallery)\b/i.test(t)) {
    return 'Graphic selection in progress - browse wagnerskis.com/gallery for ideas.';
  }
  // WOOD topsheets (Pete 2026-11-02): "Hand-finished <WOOD TYPE> wood."
  // e.g. "Paduk Wood Veneer" -> "Hand-finished Padauk wood." (record spelling
  // "Paduk" normalized to the wood's correct name, Padauk, for customer copy.)
  if (/\bveneer\b/i.test(t)) {
    const type = t.replace(/\bveneer\b/gi, '').replace(/\bwood\b/gi, '').trim();
    if (type) {
      const pretty = type.replace(/\bpaduk\b/i, 'Padauk');
      return `Hand-finished ${pretty} wood.`;
    }
  }
  return t;
}
